/**
 * Task sul telefono (7/10/2026): risponde a "cosa devo fare?".
 *
 * Prima, a 390px, la pagina cominciava con quattro riquadri di numeri (Da
 * fare, Scadono oggi, In ritardo, Completati) spesso tutti a zero, poi le
 * linguette e "Cerca, ordina e filtra", e solo sotto i task. Qui:
 *   1. in grande, quanti task ho da fare, e sotto quanti sono in ritardo;
 *   2. di chi (miei, dati da me, tutti) e i completati, in una striscia sola;
 *   3. i task raggruppati per scadenza: in ritardo, oggi, nei prossimi giorni,
 *      più avanti, senza scadenza. Il cerchio a sinistra lo chiude con un
 *      tocco (stessa notifica e stesso "Annulla" del computer), la riga apre
 *      il dettaglio.
 * Gli altri filtri del computer (cliente, priorità, assegnatario, ordinamento)
 * dal telefono non servono: la ricerca trova per titolo, cliente e progetto.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { useUrlState } from '../../hooks/useUrlState';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { MEmpty, MGroup, MHero, MPage, MSearch, MSeg } from '../../components/mobile/MobileKit';
import { SCOPES, dueInfo, tasksListQuery, tasksSummaryQuery, isDone, type TaskItem, type TaskScope } from './tasksApi';
import { useTaskActions } from './useTaskActions';
import { MobileTaskList } from './TasksPage';
import './tasks-mobile.css';

type Vista = TaskScope | 'done';

const ETICHETTA_VISTA: Record<Vista, string> = {
  mine: 'Miei',
  'assigned-by-me': 'Dati da me',
  all: 'Tutti',
  done: 'Completati',
};

const TITOLO_HERO: Record<TaskScope, string> = {
  mine: 'I miei task da fare',
  'assigned-by-me': 'Task che ho dato agli altri, ancora da fare',
  all: 'Task da fare, di tutti',
};

/** I gruppi, nell'ordine in cui servono. */
function gruppoDi(t: TaskItem): { chiave: string; titolo: string; ordine: number } {
  const giorni = dueInfo(t.dueDate).days;
  if (giorni == null) return { chiave: 'senza', titolo: 'Senza scadenza', ordine: 4 };
  if (giorni < 0) return { chiave: 'ritardo', titolo: 'In ritardo', ordine: 0 };
  if (giorni === 0) return { chiave: 'oggi', titolo: 'Oggi', ordine: 1 };
  if (giorni <= 7) return { chiave: 'settimana', titolo: 'Nei prossimi 7 giorni', ordine: 2 };
  return { chiave: 'dopo', titolo: 'Più avanti', ordine: 3 };
}

export function TasksMobile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const [, setSearchParams] = useSearchParams();
  const canWrite = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.TASKS)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  // Stessi parametri nell'indirizzo della pagina del computer: tab, scope, q.
  const [tabParam] = useUrlState<string>('tab', 'open');
  const [scopeParam] = useUrlState<string>('scope', 'mine');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const scope: TaskScope = (SCOPES as string[]).includes(scopeParam) ? (scopeParam as TaskScope) : 'mine';
  const fatti = tabParam === 'done';
  const vista: Vista = fatti ? 'done' : scope;

  const { data: summary } = useQuery(tasksSummaryQuery(scope));
  const { data, isLoading } = useQuery(tasksListQuery({
    scope,
    search: search.trim() || undefined,
    status: fatti ? 'DONE' : undefined,
    includeDone: fatti,
    sort: fatti ? 'completati' : 'scadenza',
    page: 1,
    pageSize: 200,
  }));
  const { complete, reopen } = useTaskActions();
  const busy = complete.isPending || reopen.isPending;
  const items = data?.items ?? [];

  const scegli = (v: Vista) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (v === 'done') sp.set('tab', 'done');
      else {
        sp.delete('tab');
        if (v === 'mine') sp.delete('scope'); else sp.set('scope', v);
      }
      return sp;
    }, { replace: true });
  };

  const gruppi = useMemo(() => {
    if (fatti) return [{ chiave: 'fatti', titolo: 'Completati', ordine: 0, items }];
    const m = new Map<string, { chiave: string; titolo: string; ordine: number; items: TaskItem[] }>();
    for (const t of items) {
      const g = gruppoDi(t);
      if (!m.has(g.chiave)) m.set(g.chiave, { ...g, items: [] });
      m.get(g.chiave)!.items.push(t);
    }
    return [...m.values()].sort((a, b) => a.ordine - b.ordine);
  }, [items, fatti]);

  const toggle = (t: TaskItem) => (isDone(t.status)
    ? reopen.mutate({ id: t.id, title: t.title })
    : complete.mutate({ id: t.id, title: t.title, prevStatus: t.status, dueDate: t.dueDate, recurrence: t.recurrence }));

  const ritardo = summary?.overdue ?? 0;
  const oggi = summary?.dueToday ?? 0;

  return (
    <>
      <MPage>
        {fatti ? (
          <MHero
            label="Task completati questa settimana"
            value={summary?.doneThisWeek ?? 0}
            sub="da lunedì; qui sotto tutti i completati, i più recenti per primi"
          />
        ) : (
          <MHero
            label={TITOLO_HERO[scope]}
            value={summary?.todo ?? '–'}
            sub={ritardo > 0
              ? <><span className="is-alert">{ritardo} in ritardo</span>{oggi > 0 && ` · ${oggi} ${oggi === 1 ? 'scade' : 'scadono'} oggi`}</>
              : oggi > 0 ? `${oggi} ${oggi === 1 ? 'scade' : 'scadono'} oggi, niente in ritardo` : 'niente in ritardo'}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--s-3)' }}>
          <MSeg<Vista>
            value={vista}
            onChange={scegli}
            options={(['mine', 'assigned-by-me', 'all', 'done'] as Vista[]).map((k) => ({ key: k, label: ETICHETTA_VISTA[k] }))}
          />
          <MSearch value={search} onChange={setSearch} placeholder="Cerca per titolo, cliente o progetto" />
        </div>

        {isLoading && !data ? (
          <MGroup title="Task"><MEmpty>Carico i task…</MEmpty></MGroup>
        ) : items.length === 0 ? (
          <MGroup title={fatti ? 'Completati' : 'Da fare'}>
            <MEmpty>
              {search.trim()
                ? `Nessun task con "${search.trim()}".`
                : fatti ? 'Nessun task completato, per ora.' : canWrite ? 'Niente da fare. Il bottone in basso ne crea uno nuovo.' : 'Niente da fare.'}
            </MEmpty>
          </MGroup>
        ) : (
          gruppi.map((g) => (
            <MGroup key={g.chiave} title={g.titolo} count={g.items.length}>
              <MobileTaskList
                items={g.items}
                canWrite={canWrite}
                busy={busy}
                onOpen={(id) => navigate(`/tasks/${id}`)}
                onToggle={toggle}
                senzaScadenza={g.chiave === 'oggi' || g.chiave === 'senza'}
              />
            </MGroup>
          ))
        )}
      </MPage>

      {canWrite && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newTask')}>
            <Icon name="plus" /> Nuovo task
          </button>
        </div>
      )}
    </>
  );
}
